import React, { useState } from 'react';
import { Box, Drawer, Typography, Button, IconButton, Divider } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import moment from "moment";
import { scrollCSS } from "../../../App";

const BURGUNDY = '#A72C68';
const PINK_BG = '#FAEFF5';
const PINK_BORDER = '#F0CDDB';

const CalenderDrawer = (props) => {
  const { appointmentLists, setOpen, setOpenAppointmentDetail } = props;

  const [expanded, setExpanded] = useState(false);
  const [expandedAppointments, setExpandedAppointments] = useState({});
  const toggleShowMore = (id) => {
    setExpandedAppointments(prev => ({ ...prev, [id]: !prev[id] }));
  };
  const handleToggleExpand = () => setExpanded(!expanded);
  const MAX_LENGTH = 30;
  const handleDrawerClose = () => setOpen(false);

  const headerDate = appointmentLists?.length > 0
    ? moment(appointmentLists[0]?.date).format('dddd, MMMM DD, YYYY')
    : '';

  return (
    <Drawer
      ModalProps={{ BackdropProps: { invisible: true } }}
      variant="temporary"
      open={appointmentLists !== null}
      anchor={"right"}
      onClose={handleDrawerClose}
      sx={{
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          top: '69px',
          // Constrain to the area below the 69px offset and scroll inside — otherwise
          // the paper keeps MUI's default height:100% and its bottom (and the last
          // appointment) falls below the viewport with no way to reach it.
          height: 'calc(100vh - 69px)',
          maxHeight: 'calc(100vh - 69px)',
          overflowY: 'auto',
          width: { xs: '85%', sm: '380px', md: '420px' },
          maxWidth: '100vw',
          background: '#fff',
          borderLeft: `1px solid ${PINK_BORDER}`,
          ...scrollCSS,
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { background: '#fff' },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '4px',
            background: PINK_BORDER,
          },
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 2,
        background: '#fff',
        px: { xs: 2, sm: 2.5 }, py: 1.75,
        borderBottom: `1px solid ${PINK_BORDER}`,
        display: 'flex', alignItems: 'flex-start', gap: 1.25,
      }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '10px',
          background: PINK_BG, border: `1px solid ${PINK_BORDER}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <EventAvailableIcon sx={{ color: BURGUNDY, fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: '11px', fontWeight: 700, color: BURGUNDY,
            letterSpacing: '0.5px', textTransform: 'uppercase',
          }}>
            Appointment for
          </Typography>
          <Typography sx={{
            fontSize: '15px', fontWeight: 700, color: '#1a1a2e',
            mt: '2px', lineHeight: 1.25,
          }}>
            {headerDate}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleDrawerClose}
          sx={{
            width: 32, height: 32,
            borderRadius: '8px',
            color: '#6B7280',
            border: `1px solid ${PINK_BORDER}`,
            '&:hover': { background: PINK_BG, color: BURGUNDY },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        {appointmentLists?.length > 0 && appointmentLists.map((appointment, index) => {
          const serviceNames = appointment?.service_name || [];
          const isExpanded = expandedAppointments[appointment.id];
          const visibleServices = isExpanded ? serviceNames : serviceNames.slice(0, 1);
          const statusColor = appointment?.status_color || BURGUNDY;
          const consultSuffix =
            appointment.type === 'Booking'
              ? ''
              : appointment?.type === 'Consultation'
                ? ' (In Person Consultation)'
                : ' (Virtual)';

          return (
            <Box
              key={index}
              onClick={() => {
                setOpen(false);
                setOpenAppointmentDetail(appointment?.id);
              }}
              sx={{
                position: 'relative',
                cursor: 'pointer',
                mb: 1.5,
                p: { xs: 1.25, sm: 1.5 },
                pl: { xs: 1.75, sm: 2 },
                background: '#fff',
                border: `1px solid ${PINK_BORDER}`,
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(167,44,104,0.04)',
                transition: 'all 0.15s ease',
                '&:hover': {
                  borderColor: BURGUNDY,
                  background: PINK_BG,
                  boxShadow: '0 4px 12px rgba(167,44,104,0.10)',
                  transform: 'translateY(-1px)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 10,
                  bottom: 10,
                  left: 0,
                  width: '4px',
                  borderRadius: '0 4px 4px 0',
                  background: statusColor,
                },
              }}
            >
              <Typography sx={{
                fontSize: '15px', fontWeight: 700, color: '#1a1a2e',
                textTransform: 'capitalize', lineHeight: 1.3,
              }}>
                {appointment?.customer?.name}
              </Typography>

              {serviceNames.length > 0 && (
                <Typography sx={{ fontSize: '13px', color: '#4b5563', mt: '4px' }}>
                  {visibleServices.map((name, i) => (
                    <span key={i}>
                      ({name}{consultSuffix})
                      {i < visibleServices.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                  {serviceNames.length > 1 && (
                    <Button
                      onClick={(e) => { e.stopPropagation(); toggleShowMore(appointment.id); }}
                      sx={{
                        ml: 0.5, textTransform: 'none', fontSize: '12px',
                        color: BURGUNDY, fontWeight: 600,
                        padding: 0, minWidth: 'auto',
                        display: 'inline', verticalAlign: 'baseline',
                        '&:hover': { background: 'transparent', textDecoration: 'underline' },
                      }}
                    >
                      {isExpanded ? 'Show Less' : '… Show More'}
                    </Button>
                  )}
                </Typography>
              )}

              {appointment?.appointment_customer_note?.description && (
                <Box sx={{
                  mt: 1,
                  p: 1,
                  background: PINK_BG,
                  border: `1px solid ${PINK_BORDER}`,
                  borderRadius: '8px',
                }}>
                  <Typography sx={{
                    fontSize: '11px', fontWeight: 700,
                    color: BURGUNDY, letterSpacing: '0.3px',
                    textTransform: 'uppercase', mb: '2px',
                  }}>
                    Notes
                  </Typography>
                  <Typography
                    onClick={(e) => {
                      if (appointment.appointment_customer_note.description.length > MAX_LENGTH) {
                        e.stopPropagation();
                        handleToggleExpand();
                      }
                    }}
                    sx={{
                      fontSize: '12px', color: '#374151', lineHeight: 1.4,
                      cursor: appointment.appointment_customer_note.description.length > MAX_LENGTH ? 'pointer' : 'default',
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: expanded ? 'normal' : 'nowrap',
                    }}
                  >
                    {expanded
                      ? appointment.appointment_customer_note.description
                      : appointment.appointment_customer_note.description.slice(0, MAX_LENGTH) +
                        (appointment.appointment_customer_note.description.length > MAX_LENGTH ? '…' : '')}
                  </Typography>
                </Box>
              )}

              <Box sx={{
                mt: 1,
                display: 'flex', flexDirection: 'column', gap: 0.5,
                color: '#6B7280',
              }}>
                {appointment?.customer?.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <EmailOutlinedIcon sx={{ fontSize: 14, color: BURGUNDY, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '12px', wordBreak: 'break-all' }}>
                      {appointment.customer.email}
                    </Typography>
                  </Box>
                )}
                {appointment?.customer?.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <LocalPhoneOutlinedIcon sx={{ fontSize: 14, color: BURGUNDY, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '12px' }}>
                      {appointment.customer.phone}
                    </Typography>
                  </Box>
                )}
                {(appointment?.start_time || appointment?.end_time) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <AccessTimeIcon sx={{ fontSize: 14, color: BURGUNDY, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1a1a2e' }}>
                      {appointment.start_time} {appointment.end_time && `– ${appointment.end_time}`}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}

        {(!appointmentLists || appointmentLists.length === 0) && (
          <Box sx={{
            mt: 6, textAlign: 'center',
            color: '#6B7280',
          }}>
            <Typography sx={{ fontWeight: 600 }}>No appointments for this day</Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default CalenderDrawer;
